/**
 * Server-Sent Events (SSE) Type System
 *
 * Provides type-safe enums and discriminated unions for SSE event handling
 * across the application. Replaces hardcoded string literals with strongly
 * typed event types.
 */

/**
 * Message type for differentiating between user, assistant, and system messages.
 * - "user": User input messages
 * - "assistant": Normal LLM responses (default)
 * - "system": System notifications (routing info, status messages)
 */
export type MessageType = "user" | "assistant" | "system";

export enum SSEEventType {
  // Content streaming events
  OUTPUT = "output",
  MESSAGE = "message",

  // Completion events
  WORKFLOW_COMPLETED = "workflow_completed",
  MESSAGE_COMPLETED = "message_completed",
  REGENERATION_COMPLETED = "regeneration_completed",

  // Regeneration events
  REGENERATION = "regeneration",
  REGENERATION_STARTED = "regeneration_started",

  // Control events
  ERROR = "error",
  INPUT = "input",
  HEARTBEAT = "heartbeat",
}

/**
 * Base SSE event structure with flexible message ID fields
 * Supports various backend formats: message_id, messageId, data.message_id
 */
export interface BaseSSEEvent {
  type: SSEEventType;
  message_id?: string;
  messageId?: string;
  message_type?: MessageType;
  data?: {
    message_id?: string;
    messageId?: string;
    message_type?: MessageType;
    [key: string]: any;
  };
}

/**
 * SSE Message Payloads - Discriminated Union Types
 * Each event type has its specific payload structure
 */
export type SSEMessagePayload =
  | {
      type: SSEEventType.OUTPUT | SSEEventType.MESSAGE | SSEEventType.REGENERATION;
      data: string;
      message_id?: string;
      messageId?: string;
      message_type?: MessageType;
      chunk_number?: number;
    }
  | {
      type:
        | SSEEventType.WORKFLOW_COMPLETED
        | SSEEventType.MESSAGE_COMPLETED
        | SSEEventType.REGENERATION_COMPLETED
        | SSEEventType.REGENERATION_STARTED
        | SSEEventType.INPUT;
      message_id?: string;
      messageId?: string;
      message_type?: MessageType;
      data?: {
        message_id?: string;
        messageId?: string;
        message_type?: MessageType;
        [key: string]: any;
      };
    }
  | {
      type: SSEEventType.ERROR;
      data?: string | { message?: string; error?: string; [key: string]: any };
      message_id?: string;
      messageId?: string;
      message_type?: MessageType;
    };

/**
 * SSE Event with both data and event type fields
 * Handles the current flexible parsing where both data.type and event.type are checked
 */
export interface SSEEvent {
  type?: SSEEventType | string; // event.type field
  data: string; // Raw event data (JSON stringified payload)
}

/**
 * Parsed SSE Event Data
 * Represents the parsed JSON payload from event.data
 */
export type ParsedSSEEventData = SSEMessagePayload;

/**
 * Type Guards for Runtime Type Checking
 */

/**
 * Checks if event is a streaming content event (output, message, or regeneration)
 */
export function isStreamingEvent(payload: any): payload is SSEMessagePayload & {
  type: SSEEventType.OUTPUT | SSEEventType.MESSAGE | SSEEventType.REGENERATION;
} {
  return (
    payload &&
    typeof payload === "object" &&
    (payload.type === SSEEventType.OUTPUT ||
      payload.type === SSEEventType.MESSAGE ||
      payload.type === SSEEventType.REGENERATION) &&
    typeof payload.data === "string"
  );
}

/**
 * Checks if event is a message completion event
 */
export function isMessageCompletedEvent(
  payload: any,
): payload is SSEMessagePayload & { type: SSEEventType.MESSAGE_COMPLETED } {
  return payload && typeof payload === "object" && payload.type === SSEEventType.MESSAGE_COMPLETED;
}

/**
 * Checks if event is any completion event (workflow, message, or regeneration)
 */
export function isCompletionEvent(payload: any): payload is SSEMessagePayload & {
  type:
    | SSEEventType.WORKFLOW_COMPLETED
    | SSEEventType.MESSAGE_COMPLETED
    | SSEEventType.REGENERATION_COMPLETED;
} {
  return (
    payload &&
    typeof payload === "object" &&
    (payload.type === SSEEventType.WORKFLOW_COMPLETED ||
      payload.type === SSEEventType.MESSAGE_COMPLETED ||
      payload.type === SSEEventType.REGENERATION_COMPLETED)
  );
}

/**
 * Utility function to extract message ID from various payload formats
 * Handles the flexible message ID fields used across the application
 */
export function extractMessageId(payload: any): string | undefined {
  if (!payload || typeof payload !== "object") {
    return undefined;
  }

  // Direct fields
  if (typeof payload.message_id === "string") {
    return payload.message_id;
  }

  if (typeof payload.messageId === "string") {
    return payload.messageId;
  }

  // Nested in data object
  if (payload.data && typeof payload.data === "object") {
    if (typeof payload.data.message_id === "string") {
      return payload.data.message_id;
    }

    if (typeof payload.data.messageId === "string") {
      return payload.data.messageId;
    }
  }

  return undefined;
}

/**
 * Utility function to extract message type from SSE payload.
 * Returns "assistant" as default for backward compatibility.
 */
export function extractMessageType(payload: any): MessageType {
  if (!payload || typeof payload !== "object") {
    return "assistant";
  }

  // Direct field
  if (
    payload.message_type === "user" ||
    payload.message_type === "assistant" ||
    payload.message_type === "system"
  ) {
    return payload.message_type;
  }

  // Nested in data object
  if (payload.data && typeof payload.data === "object") {
    if (
      payload.data.message_type === "user" ||
      payload.data.message_type === "assistant" ||
      payload.data.message_type === "system"
    ) {
      return payload.data.message_type;
    }
  }

  // Default to "assistant" for backward compatibility
  return "assistant";
}
